import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  notificationKeys,
  notificationsApi,
  type NotificationList,
} from "@/lib/api/notifications";
import { useAuthStore } from "@/store/authStore";

/**
 * How often the unread count is re-read while the tab is in view.
 *
 * The same minute `useRoleSync` polls the profile on: notifications are raised
 * by alerts and Desk, which a reader expects to see within the minute, and a
 * hidden tab is not polled at all (TanStack's default), so the cost is one
 * count query per open, visible tab per minute.
 */
export const NOTIFICATION_POLL_MS = 60_000;

function useUserId(): string | null {
  const id = useAuthStore((s) => s.user?.id);
  return id != null ? String(id) : null;
}

/** The server's unread count for the signed-in user. */
export function useUnreadNotificationCount() {
  const userId = useUserId();
  return useQuery({
    queryKey: notificationKeys.unreadCount(userId),
    queryFn: notificationsApi.unreadCount,
    enabled: userId != null,
    refetchInterval: NOTIFICATION_POLL_MS,
    refetchIntervalInBackground: false,
    // The app turns focus refetching off because a cost read can be a paid
    // provider call. This is one count against our own database, and coming
    // back to the tab is exactly when the reader looks at the badge.
    refetchOnWindowFocus: true,
    staleTime: NOTIFICATION_POLL_MS / 2,
  });
}

/**
 * The newest notifications. Mounted only while the panel is open, and stale at
 * once, so each opening reads the list again: the poll keeps only the count
 * current.
 */
export function useNotificationList() {
  const userId = useUserId();
  return useQuery({
    queryKey: notificationKeys.list(userId),
    queryFn: notificationsApi.list,
    enabled: userId != null,
    staleTime: 0,
  });
}

/**
 * Shared shape of both mark-read mutations.
 *
 * Only the list is updated ahead of the server: a row's read state is what the
 * reader just asked for, so it changes under their hand. The count is never
 * predicted, because the badge shows a number only the server gave; it is
 * re-read when the write settles, and a refused write puts the rows back.
 */
function useMarkRead<TVariables>(
  mutationFn: (variables: TVariables) => Promise<unknown>,
  markRow: (variables: TVariables, id: number) => boolean,
  failure: string,
) {
  const userId = useUserId();
  const queryClient = useQueryClient();
  const listKey = notificationKeys.list(userId);

  return useMutation({
    mutationFn,
    onMutate: async (variables: TVariables) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<NotificationList>(listKey);
      if (previous) {
        queryClient.setQueryData<NotificationList>(listKey, {
          ...previous,
          results: previous.results.map((n) =>
            markRow(variables, n.id) ? { ...n, is_read: true } : n,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(listKey, context.previous);
      }
      toast.error(failure);
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: notificationKeys.forUser(userId),
      }),
  });
}

export function useMarkNotificationRead() {
  return useMarkRead(
    (id: number) => notificationsApi.markRead(id),
    (id, rowId) => rowId === id,
    "Could not mark the notification read. Try again.",
  );
}

export function useMarkAllNotificationsRead() {
  return useMarkRead<void>(
    () => notificationsApi.markAllRead(),
    () => true,
    "Could not mark your notifications read. Try again.",
  );
}
